// Verwaltung (nur GQL): Welche DB-Nutzer sehen diesen Standort? Zuordnen und Zuordnung lösen.
import { useCallback, useEffect, useState } from 'react';
import type { DbPortalNutzerEintrag } from '@gql/shared-types';
import { api } from '../api/client.js';

interface Zugeordnet {
  id: string;
  name: string;
  email: string;
  aktiv: boolean;
}

export function StandortNutzer({ standortId }: { standortId: string }) {
  const [zugeordnet, setZugeordnet] = useState<Zugeordnet[] | null>(null);
  const [alle, setAlle] = useState<DbPortalNutzerEintrag[]>([]);
  const [auswahl, setAuswahl] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    Promise.all([
      api<{ nutzer: Zugeordnet[] }>(`/admin/standorte/${standortId}/nutzer`),
      api<{ nutzer: DbPortalNutzerEintrag[] }>('/admin/nutzer'),
    ])
      .then(([z, a]) => {
        setZugeordnet(z.nutzer);
        setAlle(a.nutzer.filter((n) => n.rolle === 'db_user' && n.aktiv));
      })
      .catch((f: Error) => setFehler(f.message));
  }, [standortId]);
  useEffect(laden, [laden]);

  async function aendern(aktion: () => Promise<unknown>) {
    setLaeuft(true);
    setFehler(null);
    try {
      await aktion();
      setAuswahl('');
      laden();
    } catch (f) {
      setFehler((f as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  const frei = alle.filter((n) => !zugeordnet?.some((z) => z.id === n.id));
  return (
    <div className="standort-nutzer">
      <h3>Zugeordnete Nutzer</h3>
      {zugeordnet && zugeordnet.length === 0 && <p className="zelle-hinweis">Niemand zugeordnet – nur GQL sieht diesen Standort.</p>}
      {zugeordnet && zugeordnet.length > 0 && (
        <ul className="upload-liste">
          {zugeordnet.map((n) => (
            <li key={n.id}>
              <span className="zuordnung-name">
                {n.name}
                {!n.aktiv && ' (gesperrt)'}
              </span>
              <button
                type="button"
                className="knopf-klein-text"
                disabled={laeuft}
                aria-label={`Zuordnung von ${n.name} lösen`}
                onClick={() => void aendern(() => api(`/admin/standorte/${standortId}/nutzer/${n.id}`, { methode: 'DELETE' }))}
              >
                Zuordnung lösen
              </button>
            </li>
          ))}
        </ul>
      )}
      {frei.length > 0 && (
        <div className="filterzeile">
          <label className="feld">
            <span className="feld-label">Nutzer zuordnen</span>
            <select value={auswahl} onChange={(e) => setAuswahl(e.target.value)}>
              <option value="">– auswählen –</option>
              {frei.map((n) => (
                <option key={n.id} value={n.id}>
                  {[n.organisation, n.anzeigename ?? n.email].filter(Boolean).join(' – ')}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="knopf-sekundaer knopf-auto"
            disabled={!auswahl || laeuft}
            onClick={() => void aendern(() => api(`/admin/standorte/${standortId}/nutzer`, { methode: 'POST', json: { nutzerId: auswahl } }))}
          >
            Zuordnen
          </button>
        </div>
      )}
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
    </div>
  );
}
